import React, { useState, useEffect } from "react";
import "./BlouseCustomizer.css";
import API_URL from "../apiConfig";

const API_BASE_URL = API_URL;

const BlouseCustomizer = ({ onBack }) => {
  const [bases, setBases] = useState([]);
  const [selectedBase, setSelectedBase] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [workComponents, setWorkComponents] = useState({}); // Dynamic object: { 'Neck Work': [...], 'Sleeve Patch': [...] }
  const [selections, setSelections] = useState({}); // Dynamic object for selections
  const [referenceBlouses, setReferenceBlouses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBases();
  }, []);

  useEffect(() => {
    if (selectedBase) {
      fetchComponents();
      fetchReferences();
    }
  }, [selectedBase]);

  const fetchBases = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/customization/bases`);
      const data = await res.json();
      setBases(data);
      if (data.length > 0) {
        setSelectedBase(data[0]);
        setSelectedColor(data[0].colors[0]);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const fetchComponents = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/customization/components`);
      const data = await res.json();

      // Filter components based on compatibility
      const compatible = data.filter(
        (c) =>
          c.compatibleBases.includes(selectedBase._id) ||
          c.compatibleNeckTypes.includes(selectedBase.neckType) ||
          c.compatibleSleeveTypes.includes(selectedBase.sleeveType),
      );

      // Group dynamically by type
      const grouped = {};
      compatible.forEach((c) => {
        if (!grouped[c.type]) grouped[c.type] = [];
        grouped[c.type].push(c);
      });

      setWorkComponents(grouped);

      // Initialize empty selections for each group
      const initialSelections = {};
      Object.keys(grouped).forEach((k) => (initialSelections[k] = null));
      setSelections(initialSelections);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchReferences = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/customization/references?baseBlouseId=${selectedBase._id}`,
      );
      const data = await res.json();
      setReferenceBlouses(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleBaseChange = (base) => {
    setSelectedBase(base);
    setSelectedColor(base.colors[0]);
    setSelections({ Neck: null, Sleeve: null, Back: null });
  };

  const calculateTotal = () => {
    let total = selectedBase ? selectedBase.basePrice : 0;
    Object.values(selections).forEach((item) => {
      if (item) total += item.additionalPrice || 0;
    });
    return total;
  };

  const handleAddToCart = () => {
    alert("Customized blouse added to cart!");
    // Implement actual cart logic here
  };

  if (loading)
    return <div className="customizer-loading">Loading Customizer...</div>;

  return (
    <div className="customizer-container">
      <div className="customizer-header">
        <button className="back-btn" onClick={onBack}>
          ← Back
        </button>
        <h1>Customize Your Blouse</h1>
      </div>

      <div className="customizer-main">
        {/* Left: Preview Section */}
        <div className="preview-section">
          <div className="canvas-area">
            {selectedColor && (
              <img
                src={
                  selectedColor.images[0].startsWith("http")
                    ? selectedColor.images[0]
                    : `${API_BASE_URL}${selectedColor.images[0]}`
                }
                alt="Base Blouse"
                className="base-img"
              />
            )}
            {/* Overlays for all dynamic work selections */}
            {Object.keys(selections).map(
              (type) =>
                selections[type] && (
                  <img
                    key={type}
                    src={`${API_BASE_URL}${selections[type].images[0]}`}
                    alt={type}
                    className={`overlay-item overlay-${type.toLowerCase().replace(/\s+/g, "-")}`}
                  />
                ),
            )}
          </div>

          <div className="reference-suggestion">
            <h3>Reference: How it looks when stitched</h3>
            <div className="ref-images">
              {referenceBlouses.length > 0 ? (
                referenceBlouses.map((ref) => (
                  <div key={ref._id} className="ref-card">
                    <img
                      src={`${API_BASE_URL}${ref.images[0]}`}
                      alt="Reference"
                    />
                    <p>{ref.name}</p>
                  </div>
                ))
              ) : (
                <p className="no-ref">
                  No reference images for this combination yet.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Right: Controls Section */}
        <div className="controls-section">
          {/* ... Base and Color selections remain same ... */}
          <section className="control-group">
            <h2>1. Choose Base Blouse</h2>
            <div className="base-grid">
              {bases.map((base) => (
                <div
                  key={base._id}
                  className={`base-card ${selectedBase?._id === base._id ? "active" : ""}`}
                  onClick={() => handleBaseChange(base)}
                >
                  <img
                    src={
                      base.colors[0].images[0].startsWith("http")
                        ? base.colors[0].images[0]
                        : `${API_BASE_URL}${base.colors[0].images[0]}`
                    }
                    alt={base.name}
                  />
                  <div className="base-info">
                    <span>{base.name}</span>
                    <small>
                      {base.fabricType} | {base.neckType}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="control-group">
            <h2>2. Select Color</h2>
            <div className="color-circles">
              {selectedBase?.colors.map((color) => (
                <div
                  key={color.colorName}
                  className={`color-circle ${selectedColor?.colorName === color.colorName ? "active" : ""}`}
                  style={{ backgroundColor: color.colorName.toLowerCase() }}
                  onClick={() => setSelectedColor(color)}
                  title={color.colorName}
                />
              ))}
            </div>
          </section>

          <section className="control-group">
            <h2>3. Add Work Components</h2>

            {Object.keys(workComponents).map((type) => (
              <div key={type} className="work-type-section">
                <h3>{type}</h3>
                <div className="work-grid">
                  <div
                    className={`work-item none ${!selections[type] ? "active" : ""}`}
                    onClick={() =>
                      setSelections({ ...selections, [type]: null })
                    }
                  >
                    <span>None</span>
                  </div>
                  {workComponents[type].map((comp) => (
                    <div
                      key={comp._id}
                      className={`work-item ${selections[type]?._id === comp._id ? "active" : ""}`}
                      onClick={() =>
                        setSelections({ ...selections, [type]: comp })
                      }
                    >
                      <img
                        src={`${API_BASE_URL}${comp.images[0]}`}
                        alt={comp.name}
                      />
                      <div className="work-info">
                        <span>{comp.name}</span>
                        {comp.additionalPrice > 0 && (
                          <small>+₹{comp.additionalPrice}</small>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>

          <div className="pricing-footer">
            <div className="total-price">
              <span>Total Price</span>
              <h3>₹{calculateTotal()}</h3>
            </div>
            <button className="add-to-cart-btn" onClick={handleAddToCart}>
              Add Customized Blouse to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlouseCustomizer;
